import React from "react";
import {
  ChartBarSquareIcon,
  ShoppingCartIcon,
  CubeIcon,
  UsersIcon,
  MegaphoneIcon,
  PuzzlePieceIcon,
  DocumentChartBarIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftEllipsisIcon,
  BookOpenIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  CreditCardIcon,
  ClipboardDocumentListIcon,
  LifebuoyIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  SwatchIcon,
  CpuChipIcon,
} from "@heroicons/react/24/outline";
import { Database, Activity } from "lucide-react";

import type { Page, APIModule } from "./types.ts";

export interface SubNavigationItem {
  name: string;
  page: Page;
  isMvp?: boolean;
}

export interface NavigationItem {
  name: string;
  icon: any;
  page?: Page;
  isMvp?: boolean;
  children?: SubNavigationItem[];
}

export const NAVIGATION_ITEMS: NavigationItem[] = [
  {
    name: "Dashboard",
    page: "Dashboard",
    icon: ChartBarSquareIcon,
    isMvp: true,
  },
  { name: "Orders", page: "Orders", icon: ShoppingCartIcon, isMvp: true },
  { name: "Products", page: "Products", icon: CubeIcon, isMvp: true },
  { name: "Customers", page: "Customers", icon: UsersIcon, isMvp: true },
  { name: "Marketing", page: "Marketing", icon: MegaphoneIcon, isMvp: true },
  {
    name: "Unified Inbox",
    page: "Unified Inbox",
    icon: ChatBubbleLeftEllipsisIcon,
    isMvp: true,
  },
  { name: "Channels", page: "Channels", icon: PuzzlePieceIcon, isMvp: true },
  { name: "Reports", page: "Reports", icon: DocumentChartBarIcon, isMvp: true },
  {
    name: "Logistics",
    icon: TruckIcon,
    isMvp: true,
    children: [
      { name: "Shipments", page: "Shipments", isMvp: true },
      {
        name: "Warehouse Inventory",
        page: "Warehouse Inventory",
        isMvp: false,
      },
    ],
  },
  {
    name: "Payments",
    icon: CurrencyDollarIcon,
    isMvp: true,
    children: [
      { name: "Transactions", page: "Transactions", isMvp: true },
      { name: "Invoice Management", page: "Invoices", isMvp: true },
      { name: "Payment Gateways", page: "Payment Gateways", isMvp: true },
    ],
  },
  {
    name: "Team Management",
    page: "Team Management",
    icon: UserGroupIcon,
    isMvp: true,
  },
  { name: "Billing", page: "Billing", icon: CreditCardIcon, isMvp: true },
  { name: "Settings", page: "Settings", icon: Cog6ToothIcon, isMvp: true },
  {
    name: "Knowledge Hub",
    page: "Knowledge Hub",
    icon: BookOpenIcon,
    isMvp: true,
  },
  { name: "Support Center", page: "Support", icon: LifebuoyIcon, isMvp: true },
];

export interface AdminSubNavigationItem {
  name: string;
  page: Page;
}

export interface AdminNavigationItem {
  name: string;
  icon: any;
  page?: Page;
  children?: AdminSubNavigationItem[];
}

export const ADMIN_NAVIGATION_ITEMS: AdminNavigationItem[] = [
  { name: "Super Admin", page: "Admin Dashboard", icon: ChartBarSquareIcon },
  { name: "Clients", page: "Admin Clients", icon: UsersIcon },
  { name: "User Management", page: "Admin Users", icon: UserGroupIcon },
  {
    name: "Billing & Subscriptions",
    page: "Admin Billing",
    icon: CreditCardIcon,
  },
  { name: "Channels", page: "Admin Channels", icon: PuzzlePieceIcon },
  { name: "Product & Inventory", page: "Admin Inventory", icon: CubeIcon },
  { name: "Order Monitoring", page: "Admin Orders", icon: ShoppingCartIcon },
  {
    name: "Return Requests",
    page: "Admin Returns",
    icon: ClipboardDocumentListIcon,
  },
  { name: "Marketing Control", page: "Admin Marketing", icon: MegaphoneIcon },
  { name: "System Settings", page: "Admin Settings", icon: Cog6ToothIcon },
  { name: "Support Center", page: "Admin Support", icon: LifebuoyIcon },
  { name: "Logs & Audit Trails", page: "Admin Logs", icon: DocumentTextIcon },
  { name: "Operations Center", page: "Operations Center", icon: Activity },
  { name: "Bulk Shopify Sync", page: "Bulk Sync", icon: Database },
  { name: "System Health", page: "Health", icon: CpuChipIcon },
  { name: "DLQ Recovery", page: "DLQ Recovery", icon: ShieldCheckIcon },
  {
    name: "Inventory Reconciliation",
    page: "Inventory Reconciliation",
    icon: ShieldCheckIcon,
  },

  { name: "Knowledge Hub", page: "Knowledge Hub", icon: BookOpenIcon },

  { name: "Design System", page: "DesignSystem", icon: SwatchIcon },
];

export const API_DOCS_DATA: APIModule[] = [
  {
    name: "Orders API",
    endpoints: [
      {
        method: "GET",
        path: "/api/orders",
        description: "Retrieve a list of orders.",
      },
      {
        method: "GET",
        path: "/api/orders/{id}",
        description: "Retrieve a single order.",
      },
      {
        method: "POST",
        path: "/api/orders",
        description: "Create a new order.",
      },
      {
        method: "PUT",
        path: "/api/orders/{id}",
        description: "Update an existing order.",
      },
    ],
  },
  {
    name: "Products API",
    endpoints: [
      {
        method: "GET",
        path: "/api/products",
        description: "Retrieve a list of products.",
      },
      {
        method: "GET",
        path: "/api/products/{id}",
        description: "Retrieve a single product.",
      },
      {
        method: "POST",
        path: "/api/products",
        description: "Create a new product.",
      },
      {
        method: "DELETE",
        path: "/api/products/{id}",
        description: "Archive a product.",
      },
    ],
  },
  {
    name: "Customers API",
    endpoints: [
      {
        method: "GET",
        path: "/api/customers",
        description: "Retrieve a list of customers.",
      },
      {
        method: "GET",
        path: "/api/customers/{id}",
        description: "Retrieve a single customer profile.",
      },
    ],
  },
];
